import isPropValid from '@emotion/is-prop-valid'
import { LinkButton } from '@hg/stylizator/components'
import { useSizes } from '@hg/stylizator/hooks'
import { Text } from '@hg/stylizator/typography'
import { desktop, Spacing } from '@hg/stylizator/utils'
import parse from 'html-react-parser'
import * as React from 'react'
import styled, { css, useTheme } from 'styled-components'

import useHasMounted from 'app/hooks/use-has-mounted'

interface Props {
  title: React.ReactNode
  description: React.ReactNode
  buttonText: React.ReactNode
  buttonUrl: string
  image: string
  variation?: 'small' | 'large'
  buttonClassName?: string
}

const PromoBannerWithImage: React.FC<Props> = ({
  title = 'Earn passive income effortlessly',
  description = (
    <>
      Join Honeygain and collect <b>$2 starter gift</b> for free
    </>
  ),
  buttonText = 'Join Honeygain',
  buttonUrl,
  variation,
  image,
  buttonClassName,
}) => {
  const theme = useTheme()
  const { isDesktop } = useSizes()
  const hasMounted = useHasMounted()
  const isLarge = React.useMemo(
    () => (variation ? variation === 'large' : isDesktop),
    [variation, isDesktop, hasMounted],
  )

  return (
    <Background isLarge={isLarge}>
      <Image src={image} alt="" isLarge={isLarge} />

      <Spacing top={isLarge ? 0 : 2}>
        <Text type={isLarge ? 'heading_large' : 'heading_medium'}>
          <p>{title}</p>
        </Text>

        <Spacing top={isLarge ? 2 : 1.5}>
          <Text type="body">
            <p>{typeof description === 'string' ? parse(description) : description}</p>
          </Text>
        </Spacing>
      </Spacing>

      <ButtonSpacing isLarge={isLarge}>
        <LinkButton
          href={buttonUrl}
          target="_blank"
          rel="noreferrer"
          className={buttonClassName}
          type="primary"
          style={{ width: isLarge ? '200px' : '100%' }}
          role="button"
        >
          <Text type="label" color={theme.color.white}>
            <span>{buttonText}</span>
          </Text>
        </LinkButton>
      </ButtonSpacing>
    </Background>
  )
}

export default PromoBannerWithImage

const Background = styled.aside.withConfig({ shouldForwardProp: isPropValid })<{ isLarge?: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  flex-direction: column;

  width: 100%;
  box-sizing: border-box;
  padding: 40px;
  background: ${(props) => props.theme.color.blue[10]};

  ${(props) =>
    props.isLarge &&
    css`
      flex-direction: row;
      text-align: left;

      & > * + * {
        margin-left: 56px;
      }
    `}
`

const ButtonSpacing = styled.div.withConfig({ shouldForwardProp: isPropValid })<{ isLarge?: boolean }>`
  margin-top: 40px;
  width: 100%;

  @media ${desktop} {
    margin-top: 0;
    width: auto;
  }
`

const Image = styled.img.withConfig({ shouldForwardProp: isPropValid })<{ isLarge?: boolean }>`
  max-width: 176px;
  max-height: 176px;

  @media ${desktop} {
    max-width: 88px;
    max-height: 88px;
  }
`
